import axios, { AxiosError } from 'axios';
import { environment as envConfig } from '@/config/environment';
import { localStorageVariables } from '@/types/localStorageVariables';
import { sessionExpirationHandler } from '@/lib/auth/sessionExpirationHandler';
import { isLogoutInProgress } from '@/lib/auth/logout';
import { webStorage } from '../auth/storage';

// Global variable to store query parameter headers for axios
let globalAxiosHeaders: Record<string, string> = {}

// Function to update global axios headers
export const setGlobalAxiosHeaders = (headers: Record<string, string>) => {
  globalAxiosHeaders = { ...headers }
}

// Global session expiration callback
let globalSessionExpirationCallback: (() => void) | null = null;

// Function to set the session expiration callback
export const setSessionExpirationCallback = (callback: () => void) => {
  globalSessionExpirationCallback = callback;
}

// Function to check and handle session expiration
const checkSessionExpiration = () => {
  if (sessionExpirationHandler.checkSessionExpired() && globalSessionExpirationCallback) {
    globalSessionExpirationCallback();
  }
}
// Configure axios globally  
// withCredentials is required to send authentication cookies like YSISessionID
axios.defaults.timeout = 30000;
axios.defaults.withCredentials = true;

// Add a global request interceptor for logging
axios.interceptors.request.use(
  (config) => {
    if (isLogoutInProgress()) {
      return Promise.reject(new AxiosError('Request cancelled — logout in progress', AxiosError.ERR_CANCELED));
    }

    // Add X-Current-Path header with current URL path for HAR file analysis
    config.headers['X-Current-Path'] = window.location.pathname;

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Helper function to redirect to login page
const redirectToLogin = () => {
  const loginPath = `/onlineleasingstudent/content3/access/${localStorage.getItem(localStorageVariables.sitename)}/login`;
  window.location.href = loginPath;
};

// Add a global response interceptor for common error handling
axios.interceptors.response.use(
  (response) => {
    // Check for session expiration after successful response
    checkSessionExpiration();
    return response;
  },
  (error) => {
    // Check for session expiration after error response
    checkSessionExpiration();

    // Handle 401 Unauthorized - JWT expired
    if (error.response?.status === 401) {
        sessionExpirationHandler.handleJWTExpiration();
    }

    // Handle common errors here if needed
    console.error('Global axios error:', error);
    return Promise.reject(error);
  }
);

////////////////////////////  for secure api calls  ////////////////////////////

// Create axios instance with same configuration as FloorPlans API
export const apiClientSecure = axios.create({
  baseURL: envConfig.apiPrefix.rootPath,
  timeout: 50000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include Authorization header if token exists
apiClientSecure.interceptors.request.use(
  (config) => {
    if (isLogoutInProgress()) {
      return Promise.reject(new AxiosError('Request cancelled — logout in progress', AxiosError.ERR_CANCELED));
    }

    // Add X-Current-Path header with current URL path for HAR file analysis
    config.headers['X-Current-Path'] = window.location.pathname;

    // Add Authorization header with JWT token
    // access_token is a cookie automatically added during login - HttpOnly - so we can't add it here. 
    // server takes care of handling the cookie JWT token 
    // const tokenData = getStoredTokenData();
    // if (tokenData && tokenData.access_token) {
    //   config.headers.Authorization = `Bearer ${tokenData.access_token}`;
    // }

    // Add global query parameter headers
    Object.keys(globalAxiosHeaders).forEach(key => {
      config.headers[key] = globalAxiosHeaders[key];
    });

    // Remove Content-Type header for FormData to let browser set it automatically
    // This is crucial for file uploads as browser needs to set the correct boundary
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }

    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for apiClientSecure to check for null reference exceptions
apiClientSecure.interceptors.response.use(
  (response) => {
    // Check for session expiration after successful response
    checkSessionExpiration();

    // Only check if request has Authorization header (authenticated request)
    if (response.config?.headers?.Authorization) {
      const responseText = typeof response.data === 'string' ? response.data : JSON.stringify(response.data);
      if (responseText && responseText.toLowerCase().includes('object reference not set to instance of an object')) {
        console.error('Null reference exception detected - redirecting to login');
        redirectToLogin();
        return response; // Still return response to avoid breaking the request chain
      }
    }

    return response;
  },
  (error: AxiosError) => {
    // Check for session expiration after error response
    checkSessionExpiration();

    // Handle 401 Unauthorized - JWT expired
    if (error.response?.status === 401) {
        sessionExpirationHandler.handleJWTExpiration();
    }

    // Only check if request has Authorization header (authenticated request)
    if (error.config?.headers?.Authorization && error.response?.data) {
      const errorText = typeof error.response.data === 'string' ? error.response.data : JSON.stringify(error.response.data);
      if (errorText && errorText.toLowerCase().includes('object reference not set to instance of an object')) {
        console.error('Null reference exception detected in error response - redirecting to login');
        redirectToLogin();
      }
    }

    return Promise.reject(error);
  }
);

// API client for Resportal (Resident Services) endpoints
export const apiClientSecureForResportal = axios.create({
  baseURL: envConfig.apiPrefix.rootPath.replace('/onlineleasingstudent/', '/residentservicesstudent/'),
  timeout: 50000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include Authorization header if token exists for Resportal
apiClientSecureForResportal.interceptors.request.use(
  (config) => {
    // Add X-Current-Path header with current URL path for HAR file analysis
    config.headers['X-Current-Path'] = window.location.pathname;

    // Add Authorization header with JWT token
    // access_token is a cookie automatically added during login - HttpOnly - so we can't add it here. 
    // server takes care of handling the cookie JWT token 
    // const tokenData = getStoredTokenData();
    // if (tokenData && tokenData.access_token) {
    //   config.headers.Authorization = `Bearer ${tokenData.access_token}`;
    // }

    // Add global query parameter headers
    Object.keys(globalAxiosHeaders).forEach(key => {
      config.headers[key] = globalAxiosHeaders[key];
    });

    // Remove Content-Type header for FormData to let browser set it automatically
    // This is crucial for file uploads as browser needs to set the correct boundary
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }

    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for apiClientSecureForResportal to check for null reference exceptions
apiClientSecureForResportal.interceptors.response.use(
  (response) => {
    // Check for session expiration after successful response
    checkSessionExpiration();

    // Only check if request has Authorization header (authenticated request)
    if (response.config?.headers?.Authorization) {
      const responseText = typeof response.data === 'string' ? response.data : JSON.stringify(response.data);
      if (responseText && responseText.toLowerCase().includes('object reference not set to instance of an object')) {
        console.error('Null reference exception detected - redirecting to login');
        redirectToLogin();
        return response; // Still return response to avoid breaking the request chain
      }
    }

    return response;
  },
  (error: AxiosError) => {
    // Check for session expiration after error response
    checkSessionExpiration();

    // Handle 401 Unauthorized - JWT expired
    if (error.response?.status === 401) {
        sessionExpirationHandler.handleJWTExpiration();
    }

    // Only check if request has Authorization header (authenticated request)
    if (error.config?.headers?.Authorization && error.response?.data) {
      const errorText = typeof error.response.data === 'string' ? error.response.data : JSON.stringify(error.response.data);
      if (errorText && errorText.toLowerCase().includes('object reference not set to instance of an object')) {
        console.error('Null reference exception detected in error response - redirecting to login');
        redirectToLogin();
      }
    }

    return Promise.reject(error);
  }
);

// Export everything needed
export { globalAxiosHeaders };
export { axios as default }; 